import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { contentAPI } from '../../services/api'
import { clipFactoryAPI, clipFactoryErrorText, type EpisodePackage as Package } from './clipFactoryApi'
import { formatTime } from './clipUtils'

interface EpisodePackageProps {
  videoId: string
  filename: string
  pkg: Package | null
  aiAvailable: boolean
  /** A signed link to the package as a .txt file; null until one exists. */
  txtUrl: string | null
  onPackage: (pkg: Package) => void
  /** Play the video from `start`, stopping at `end`. */
  onPlay: (start: number, end: number) => void
  duration: number
}

const CopyButton = ({ text, label = 'Copy' }: { text: string; label?: string }) => {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        } catch {
          setCopied(false)
        }
      }}
      className="shrink-0 rounded-md border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100"
    >
      {copied ? 'Copied' : label}
    </button>
  )
}

const Heading = ({ children, copy, copyLabel }: { children: string; copy?: string; copyLabel?: string }) => (
  <div className="flex items-center justify-between gap-2">
    <h4 className="text-sm font-semibold text-gray-900">{children}</h4>
    {copy && <CopyButton text={copy} label={copyLabel} />}
  </div>
)

/** Titles, show notes, chapters, a YouTube description and quotes for the whole episode. */
const EpisodePackage = ({ videoId, filename, pkg, aiAvailable, txtUrl, onPackage, onPlay, duration }: EpisodePackageProps) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState(true)
  const [description, setDescription] = useState(pkg?.youtube_description || '')
  useEffect(() => setDescription(pkg?.youtube_description || ''), [pkg])
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  useEffect(() => setSaveState('idle'), [pkg, videoId])

  /**
   * A YouTube draft in Library: the first title idea, and the description as
   * edited here (or, in the quick version, which has none, the chapters).
   */
  const draftText = description.trim() || pkg?.chapters_text.trim() || ''
  const saveDraft = async () => {
    if (!pkg || !draftText) return
    setSaveState('saving')
    try {
      await contentAPI.create({
        content_type: 'youtube',
        title: pkg.titles[0] || filename,
        text: draftText,
        hashtags: pkg.hashtags,
        description: `Episode package for ${filename}.`,
        source: { kind: 'episode', video_id: videoId, filename },
      })
      setSaveState('saved')
    } catch {
      setSaveState('failed')
    }
  }

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      onPackage(await clipFactoryAPI.createPackage(videoId))
      setOpen(true)
    } catch (e) {
      setError(clipFactoryErrorText(e, "Couldn't make the episode package."))
    } finally {
      setBusy(false)
    }
  }

  const chapterTimes = pkg ? pkg.chapters_text.split('\n').map((line) => line.split(' ')[0]) : []
  const hashtags = pkg ? pkg.hashtags.map((h) => `#${h}`).join(' ') : ''

  return (
    <section className="bg-white rounded-lg shadow p-5" aria-label="Episode package">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">Episode package</h3>
        {pkg && (
          <div className="flex flex-wrap gap-2">
            {txtUrl && (
              <a
                href={txtUrl}
                download
                className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
              >
                Download .txt
              </a>
            )}
            <button
              type="button"
              onClick={create}
              disabled={busy}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              {busy ? 'Writing…' : 'Make again'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100"
            >
              {open ? 'Hide' : 'Show'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-sm text-danger-600">
          {error}
        </p>
      )}

      {!pkg ? (
        <div className="mt-2 space-y-3">
          <p className="text-sm text-gray-600">
            Everything to publish with the whole episode: title ideas, show notes, chapters with timestamps, a YouTube
            description and quotable lines
            {aiAvailable
              ? ', written by Claude from the full transcript.'
              : '. Without AI you get a quick version: chapters, quotes and titles from the clips, but no show notes or description.'}
          </p>
          <button
            type="button"
            onClick={create}
            disabled={busy}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {busy ? 'Writing the package… (up to a minute)' : 'Make episode package'}
          </button>
        </div>
      ) : (
        open && (
          <div className="mt-3 space-y-5" data-testid="episode-package">
            <p className="text-xs text-gray-500">
              {pkg.source === 'quick'
                ? 'Quick version from the transcript (not AI)'
                : 'Written by Claude from the full transcript. Check it before you publish.'}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={saveDraft}
                disabled={!draftText || saveState === 'saving'}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
              >
                {saveState === 'saving' ? 'Saving…' : 'Send to Library as a YouTube draft'}
              </button>
              {saveState === 'saved' && (
                <span className="text-xs text-success-600">
                  Saved as a draft in{' '}
                  <Link to="/content" className="underline">
                    Library
                  </Link>
                  .
                </span>
              )}
              {saveState === 'failed' && (
                <span role="alert" className="text-xs text-danger-600">
                  Couldn't save the draft. Try again.
                </span>
              )}
            </div>
            {pkg.notes.map((note) => (
              <p key={note} className="text-xs text-gray-600">
                {note}
              </p>
            ))}

            {pkg.titles.length > 0 && (
              <div className="space-y-1.5">
                <Heading>Episode title ideas</Heading>
                <ul className="space-y-1">
                  {pkg.titles.map((title) => (
                    <li key={title} className="flex items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-1.5">
                      <span className="min-w-0 break-words text-sm text-gray-900">{title}</span>
                      <CopyButton text={title} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pkg.summary && (
              <div className="space-y-1.5">
                <Heading copy={[pkg.summary, ...pkg.key_points.map((p) => `- ${p}`)].join('\n\n')}>Show notes</Heading>
                {pkg.summary.split(/\n\s*\n/).map((para) => (
                  <p key={para} className="text-sm text-gray-700">
                    {para}
                  </p>
                ))}
                {pkg.key_points.length > 0 && (
                  <ul className="list-disc space-y-0.5 pl-5 text-sm text-gray-700">
                    {pkg.key_points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {pkg.chapters.length > 0 && (
              <div className="space-y-1.5">
                <Heading copy={pkg.chapters_text} copyLabel="Copy chapters">
                  Chapters
                </Heading>
                <ul>
                  {pkg.chapters.map((chapter, k) => (
                    <li key={`${chapter.start}-${k}`}>
                      <button
                        type="button"
                        onClick={() => onPlay(chapter.start, pkg.chapters[k + 1]?.start ?? duration)}
                        title="Play this chapter"
                        className="grid w-full grid-cols-[4.5rem_1fr] gap-2 py-0.5 text-left text-sm hover:bg-gray-50"
                      >
                        <span className="font-semibold tabular-nums text-primary-600">{chapterTimes[k] || formatTime(chapter.start)}</span>
                        <span className="min-w-0 text-gray-700">{chapter.title}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pkg.youtube_description && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <label htmlFor="episode-youtube-description" className="text-sm font-semibold text-gray-900">
                    YouTube description
                  </label>
                  <CopyButton text={description} />
                </div>
                <textarea
                  id="episode-youtube-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={8}
                  className="w-full rounded-md border border-gray-300 p-2 text-sm text-gray-900"
                />
              </div>
            )}

            {pkg.quotes.length > 0 && (
              <div className="space-y-1.5">
                <Heading>Quotable lines</Heading>
                <ul className="space-y-1">
                  {pkg.quotes.map((quote) => (
                    <li key={`${quote.start}-${quote.text}`} className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-1.5">
                      <button
                        type="button"
                        onClick={() => onPlay(quote.start, Math.min(duration, quote.start + 15))}
                        title="Play from here"
                        className="shrink-0 text-sm font-semibold tabular-nums text-primary-600"
                      >
                        {formatTime(quote.start)}
                      </button>
                      <span className="min-w-0 flex-1 break-words text-sm text-gray-700">“{quote.text}”</span>
                      <CopyButton text={quote.text} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {hashtags && (
              <div className="space-y-1.5">
                <Heading copy={hashtags}>Hashtags</Heading>
                <p className="text-sm text-gray-700">{hashtags}</p>
              </div>
            )}
          </div>
        )
      )}
    </section>
  )
}

export default EpisodePackage
